import type {createQueryClient} from '@github-ui/query-client'

type PreheatQueryClient = ReturnType<typeof createQueryClient>
type PreheatRun = {promise: Promise<unknown>; callers: number; settled: boolean}

const runsByClient = new WeakMap<PreheatQueryClient, Map<string, PreheatRun>>()

/**
 * Starts `fetch` once per client and query while it is in flight; later callers wait for the same run.
 *
 * Aborting a caller only stops that caller from waiting. The run is cancelled once every waiting caller has
 * aborted, so one list unmounting cannot cancel a preheat that another caller still needs. Callers that are
 * still waiting when the run fails or is cancelled receive the error through `onError`.
 */
export async function runSharedPreheat({
  client,
  queryKey,
  fetch,
  signal,
  onError,
}: {
  client: PreheatQueryClient
  queryKey: readonly unknown[]
  fetch: () => Promise<unknown>
  signal?: AbortSignal
  onError?: (error: unknown) => void
}): Promise<void> {
  if (signal?.aborted) return

  const runs = runsByClient.get(client) ?? new Map<string, PreheatRun>()
  runsByClient.set(client, runs)
  const {queryHash} = client.defaultQueryOptions({queryKey})
  const run = runs.get(queryHash) ?? startRun(runs, queryHash, fetch)

  run.callers++
  let waiting = true
  const stopWaiting = () => {
    if (!waiting) return
    waiting = false
    run.callers--
  }
  const leftEarly = Promise.withResolvers<void>()
  const onAbort = () => {
    stopWaiting()
    if (run.settled) return
    if (run.callers > 0) {
      // Another caller still needs the run, so only this caller stops waiting.
      leftEarly.resolve()
      return
    }
    runs.delete(queryHash)
    // The run then rejects with a CancelledError, which reaches this caller below.
    void client.cancelQueries({queryKey, exact: true})
  }

  signal?.addEventListener('abort', onAbort, {once: true})
  try {
    await Promise.race([run.promise, leftEarly.promise])
  } catch (error) {
    // Preheating is best effort, so failures only reach callers that ask for them.
    onError?.(error)
  } finally {
    signal?.removeEventListener('abort', onAbort)
    stopWaiting()
  }
}

function startRun(runs: Map<string, PreheatRun>, queryHash: string, fetch: () => Promise<unknown>) {
  const run: PreheatRun = {promise: fetch(), callers: 0, settled: false}
  runs.set(queryHash, run)
  void markSettled(runs, queryHash, run)
  return run
}

// This starts waiting before any caller does, so `settled` is set before callers resume.
async function markSettled(runs: Map<string, PreheatRun>, queryHash: string, run: PreheatRun) {
  try {
    await run.promise
  } catch {
    // Waiting callers receive the error.
  } finally {
    run.settled = true
    if (runs.get(queryHash) === run) runs.delete(queryHash)
  }
}
